import ChapterBadge from "./ChapterBadge";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Gauge, Broadcast, CheckCircle, Path } from "@phosphor-icons/react";
import { Button } from "../ui/button";

const DiagnosticSection = ({ onDiagnosticClick }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const outputs = [
    { icon: Gauge, label: "Current AI readiness" },
    { icon: Broadcast, label: "Strongest source signals" },
    { icon: CheckCircle, label: "Immediate answerability" },
    { icon: Path, label: "Recommended AI activation path" },
  ];

  return (
    <section 
      ref={ref}
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 relative overflow-hidden"
      data-testid="diagnostic-section"
    >
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] radial-gradient opacity-30" />

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <ChapterBadge number="11" name="Activation" />
          <span className="mono-label">Begin Your AI Journey</span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Outfit']">
            Start your AI journey with the <span className="gradient-text">diagnostic</span>
          </h2>
          <p className="mt-4 text-[#A1A1AA] max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            OnTenacity begins by identifying your systems, strongest signals, available context, 
            and likely answerability — then shows where AI can create immediate leadership value 
            and what needs to be connected next.
          </p>
        </motion.div>

        {/* Output Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10"
        >
          {outputs.map((output, index) => (
            <div 
              key={index}
              className="flex items-center gap-3 p-4 rounded-xl bg-[#0F0F10] border border-white/5"
              data-testid={`diagnostic-output-${index + 1}`}
            >
              <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0">
                <output.icon size={20} weight="duotone" className="text-[#00E5FF]" />
              </div>
              <span className="text-[#FAFAFA] text-sm font-medium">{output.label}</span>
            </div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center"
        >
          <Button
            onClick={onDiagnosticClick}
            className="btn-primary px-10 py-5 rounded-full text-base font-semibold flex items-center justify-center gap-2 group mx-auto"
            data-testid="diagnostic-section-btn"
          >
            Start AI Diagnostic
            <ArrowRight 
              size={20} 
              className="transition-transform group-hover:translate-x-1" 
            />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default DiagnosticSection;
